import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, use, useEffect, useMemo, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EnterpriseKind } from '@commutifi/models/Enterprises';
import { FeatureFlagResponse, FeatureFlags } from '@commutifi/models/FeatureFlag';
import { IOrganization } from 'shared/interfaces/Organization';
import { AdminRouting } from 'src/routing';
import { enterpriseSelectors } from 'store/modules/enterprise';
import { accountSelectors } from 'store/modules/accounts';
import { triggerFetchFeatureFlags, triggerFetchOrganization, optimisticUpdateOrganization as reduxOptimisticUpdateOrganization } from 'store/modules/enterprise/sagas';
import { initChatWidget } from 'utils/helpers';
import { CustomBranding } from '@commutifi-fe/interfaces';
import { isFeatureEnabled as isFeatureEnabledRoot } from 'utils/helpers/featureFlags';
export { FeatureFlags };
interface ICurrentOrganizationContext {
  // Ideally we wouldn't need to use kind and orgs/enterprises would be treated the same
  // # stop discrimination!
  kind: EnterpriseKind;
  organizationId: string;
  currentOrganization?: IOrganization;
  // URL template pattern matching multiple routes. Usually useful in
  // react-router Route component (/admins/:adminId)
  basePath: string;
  // Representation of a URLs with all variables replaced (/admins/1234)
  baseRoute: string;
  surveyLinkParams?: Record<string, any>;
  isLoading: boolean;
  /**
   * Fetch the feature flags for the current organization from the organization id in the URL
   */
  fetchCurrentOrganizationFeatureFlags: () => void;
  optimisticOrganizationUpdate: (orgId: Parameters<typeof reduxOptimisticUpdateOrganization>[0], data: Parameters<typeof reduxOptimisticUpdateOrganization>[1]) => void;
  featureFlagsError: any;
  isLoadingFeatureFlags: boolean;
  featureFlags: FeatureFlagResponse | null;
  isFeatureEnabled: ReturnType<typeof isFeatureEnabledRoot>;
  refetchOrganization: () => void;
  customBranding: CustomBranding | undefined;
}
interface CurrentOrganizationProviderProps {
  children: ReactNode;
  organizationId: string;
}
const CurrentOrganizationContext = createContext<ICurrentOrganizationContext | null>(null);

/**
 * Current Profile is the word we chose to represent information
 * about one of the possible entities on our platform:
 * commuter or enterprise or organization
 */
function CurrentOrganizationProvider(t0) {
  const $ = _c(42);
  const {
    children,
    organizationId: urlOrganizationId
  } = t0;
  const currentOrganization = useSelector(enterpriseSelectors.getCurrentOrganization);
  const currentAccountId = useSelector(accountSelectors.getCurrentId);
  const surveyLinkParams = useSelector(enterpriseSelectors.getSurveyLinkParams);
  const isFetching = useSelector(enterpriseSelectors.getIsFetching);
  const isFetchingFeatureFlags = useSelector(enterpriseSelectors.getIsFetchingFeatureFlags);
  const featureFlagsError = useSelector(enterpriseSelectors.getFeatureFlagsError);
  const featureFlags = useSelector(enterpriseSelectors.getManagerAvailablePages);
  const kind = useSelector(enterpriseSelectors.getCurrentKind) || EnterpriseKind.enterprise;
  const dispatch = useDispatch();
  let t1;
  if ($[0] !== dispatch) {
    t1 = orgId => dispatch(triggerFetchOrganization(orgId));
    $[0] = dispatch;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fetchOrg = t1;
  let t2;
  if ($[2] !== dispatch) {
    t2 = orgId_0 => dispatch(triggerFetchFeatureFlags(orgId_0));
    $[2] = dispatch;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const fetchFeatureFlags = t2;
  let t3;
  if ($[4] !== dispatch) {
    t3 = (orgId_1, data) => dispatch(reduxOptimisticUpdateOrganization(orgId_1, data));
    $[4] = dispatch;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const optimisticOrganizationUpdate = t3;
  let t4;
  if ($[6] !== fetchFeatureFlags || $[7] !== urlOrganizationId) {
    t4 = () => {
      if (!urlOrganizationId) {
        return;
      }
      fetchFeatureFlags(urlOrganizationId);
    };
    $[6] = fetchFeatureFlags;
    $[7] = urlOrganizationId;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const fetchCurrentOrganizationFeatureFlags = t4;
  let t5;
  let t6;
  if ($[9] !== fetchOrg || $[10] !== urlOrganizationId) {
    t5 = () => {
      if (!urlOrganizationId) {
        return;
      }
      fetchOrg(urlOrganizationId);
    };
    t6 = [fetchOrg, urlOrganizationId];
    $[9] = fetchOrg;
    $[10] = urlOrganizationId;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[13] !== currentAccountId || $[14] !== currentOrganization) {
    t7 = () => {
      if (currentOrganization) {
        initChatWidget({
          id: `${currentAccountId}_${currentOrganization.id}`,
          organizationId: currentOrganization.id || "",
          segment: currentOrganization.segment || "no-segment",
          kind: currentOrganization.kind
        });
      }
    };
    t8 = [currentOrganization, currentAccountId];
    $[13] = currentAccountId;
    $[14] = currentOrganization;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  useEffect(t7, t8);
  let t9;
  if ($[17] !== featureFlags) {
    t9 = isFeatureEnabledRoot(featureFlags);
    $[17] = featureFlags;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const isFeatureEnabled = t9;
  const basePath = AdminRouting.root.path;
  let t10;
  if ($[19] !== urlOrganizationId) {
    t10 = AdminRouting.root.getRoute(urlOrganizationId);
    $[19] = urlOrganizationId;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const baseRoute = t10;
  const isLoading = !currentOrganization && isFetching || currentOrganization?.id !== urlOrganizationId;
  const isLoadingFeatureFlags = Boolean(isFetchingFeatureFlags && !featureFlags);
  let t11;
  if ($[21] !== fetchOrg || $[22] !== urlOrganizationId) {
    t11 = () => fetchOrg(urlOrganizationId);
    $[21] = fetchOrg;
    $[22] = urlOrganizationId;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const refetchOrganization = t11;
  const t12 = currentOrganization?.customBranding;
  let t13;
  if ($[24] !== baseRoute || $[25] !== currentOrganization || $[26] !== featureFlags || $[27] !== featureFlagsError || $[28] !== fetchCurrentOrganizationFeatureFlags || $[29] !== isFeatureEnabled || $[30] !== isLoading || $[31] !== isLoadingFeatureFlags || $[32] !== kind || $[33] !== optimisticOrganizationUpdate || $[34] !== refetchOrganization || $[35] !== surveyLinkParams || $[36] !== t12 || $[37] !== urlOrganizationId) {
    t13 = {
      kind,
      organizationId: urlOrganizationId,
      basePath,
      baseRoute,
      currentOrganization,
      optimisticOrganizationUpdate,
      surveyLinkParams,
      isLoading,
      fetchCurrentOrganizationFeatureFlags,
      featureFlagsError,
      isLoadingFeatureFlags,
      featureFlags,
      isFeatureEnabled,
      refetchOrganization,
      customBranding: t12
    };
    $[24] = baseRoute;
    $[25] = currentOrganization;
    $[26] = featureFlags;
    $[27] = featureFlagsError;
    $[28] = fetchCurrentOrganizationFeatureFlags;
    $[29] = isFeatureEnabled;
    $[30] = isLoading;
    $[31] = isLoadingFeatureFlags;
    $[32] = kind;
    $[33] = optimisticOrganizationUpdate;
    $[34] = refetchOrganization;
    $[35] = surveyLinkParams;
    $[36] = t12;
    $[37] = urlOrganizationId;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const value = t13;
  let t14;
  if ($[39] !== children || $[40] !== value) {
    t14 = <CurrentOrganizationContext value={value}>{children}</CurrentOrganizationContext>;
    $[39] = children;
    $[40] = value;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  return t14;
}
const useCurrentOrganization = () => {
  const context = use(CurrentOrganizationContext);
  if (!context) {
    throw new Error('useCurrentOrganization must be used within a CurrentOrganizationProvider');
  }
  return context;
};
const useCurrentOrganizationSafe = () => {
  const context = use(CurrentOrganizationContext);
  return context;
};
export { CurrentOrganizationProvider, useCurrentOrganization, useCurrentOrganizationSafe };